import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';

import AsyncWebappStorageCipherProxy from '../webappStorage/AsyncWebappStorageCipherProxy';
import config from './env';
import { getAmplifyAPIEndpoints, getApiRestHeaders } from '../api';
import { getCognitoOauth } from './oauth';

const LEGACY_HOSTED_UI_KEY = 'amplify-signin-with-hostedUI';
const AUTH_STORAGE_PREFIX = 'CognitoIdentityServiceProvider';

/**
 * Drop leftover Amplify v5 / interrupted v6 OAuth redirect markers when the user
 * opens the sign-in page without an OAuth callback. Otherwise attemptCompleteOAuthFlow
 * treats the visit as a cancelled redirect and emits signInWithRedirect_failure.
 */
export const clearStaleCognitoOAuthRedirectState = (): void => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('code') || searchParams.has('error')) {
        return;
    }

    const clientId = config.cognito.APP_CLIENT_ID;
    const oauthKeyPrefix = `${AUTH_STORAGE_PREFIX}.${clientId}`;
    localStorage.removeItem(LEGACY_HOSTED_UI_KEY);
    localStorage.removeItem(`${oauthKeyPrefix}.inflightOAuth`);
    localStorage.removeItem(`${oauthKeyPrefix}.oauthState`);
    localStorage.removeItem(`${oauthKeyPrefix}.oauthPKCE`);
};

export const clearCognitoSessionStorage = (): void => {
    try {
        const clientIdPrefix = `${AUTH_STORAGE_PREFIX}.${config.cognito.APP_CLIENT_ID}.`;
        Object.keys(localStorage)
            .filter((key) => key.startsWith(clientIdPrefix))
            .forEach((key) => localStorage.removeItem(key));
        localStorage.removeItem(LEGACY_HOSTED_UI_KEY);
    } catch (error) {
        console.error('Could not clear the stale Cognito session', error);
    }
};

const tokenStorage = new AsyncWebappStorageCipherProxy(localStorage);

const configureAmplify = (hasCustomSignOutURL = false): void => {
    clearStaleCognitoOAuthRedirectState();

    // Configure token storage before Amplify.configure so OAuth/custom-auth token
    // caching during the first redirect callback uses encrypted storage.
    cognitoUserPoolsTokenProvider.setKeyValueStorage(tokenStorage);

    Amplify.configure(
        {
            Auth: {
                Cognito: {
                    userPoolId: config.cognito.USER_POOL_ID,
                    userPoolClientId: config.cognito.APP_CLIENT_ID,
                    identityPoolId: config.cognito.IDENTITY_POOL_ID,
                    userPoolEndpoint: config.cognito.IDP_CUSTOM_HOST
                        ? `https://${config.cognito.IDP_CUSTOM_HOST}`
                        : undefined,
                    identityPoolEndpoint: config.cognito.IDENTITY_CUSTOM_HOST
                        ? `https://${config.cognito.IDENTITY_CUSTOM_HOST}`
                        : undefined,
                    // Recipients hit IAM-signed `public/*` endpoints (e.g. the
                    // password-service check) before completing Cognito auth.
                    // v5 signed those with guest identity-pool credentials; v6
                    // only issues guest credentials when this is enabled, so
                    // without it the request goes out unsigned and the backend
                    // sees no caller identity (`caller_id could not be None`).
                    allowGuestAccess: true,
                    loginWith: {
                        oauth: getCognitoOauth(config, hasCustomSignOutURL),
                    },
                },
            },
            API: {
                REST: getAmplifyAPIEndpoints(),
            },
        },
        {
            API: {
                REST: {
                    headers: getApiRestHeaders,
                },
            },
        },
    );
};

export default configureAmplify;
